// src/sections/Hero.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Hero.css";

import gcpLogo from "../assets/platforms/gcp-logo.png";
import cppLogo from "../assets/platforms/cpp-logo.png";
import githubLogo from "../assets/platforms/github-logo.png";
import kicadLogo from "../assets/platforms/KiCad-Logo.png";
import netlifyLogo from "../assets/platforms/netlify-logo.png";
import nodejsLogo from "../assets/platforms/nodejs-logo.svg";
import reactLogo from "../assets/platforms/React-logo.png";
import pythonLogo from "../assets/platforms/Python-logo.png";
import googleLogo from "../assets/platforms/google-logo.png";
import mysqlLogo from "../assets/platforms/MySQL-logo.png";
import postgresLogo from "../assets/platforms/postgres-logo.png";

/* ---------------- Typing hook ---------------- */
function useTypeCycle(sequences, options = {}) {
  const {
    typeMs = 22,
    deleteMs = 14,
    holdMs = 850,
    betweenMs = 240,
  } = options;

  const [seqIndex, setSeqIndex] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | holding | deleting
  const [cursorOn, setCursorOn] = useState(true);

  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  useEffect(() => {
    const blink = setInterval(() => setCursorOn((v) => !v), 560);
    return () => clearInterval(blink);
  }, []);

  useEffect(() => {
    clearTimers();
    const full = sequences[seqIndex]?.code ?? "";

    if (phase === "typing") {
      if (text.length < full.length) {
        const t = setTimeout(() => setText(full.slice(0, text.length + 1)), typeMs);
        timers.current.push(t);
      } else {
        const t = setTimeout(() => setPhase("holding"), holdMs);
        timers.current.push(t);
      }
    }

    if (phase === "holding") {
      const t = setTimeout(() => setPhase("deleting"), holdMs);
      timers.current.push(t);
    }

    if (phase === "deleting") {
      if (text.length > 0) {
        const t = setTimeout(() => setText((prev) => prev.slice(0, -1)), deleteMs);
        timers.current.push(t);
      } else {
        const t = setTimeout(() => {
          setSeqIndex((i) => (i + 1) % sequences.length);
          setPhase("typing");
        }, betweenMs);
        timers.current.push(t);
      }
    }

    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, phase, seqIndex, sequences]);

  const active = sequences[seqIndex] || sequences[0];
  return { active, text, cursorOn };
}

/* ---------------- Lightweight syntax highlight ---------------- */
const KEYWORDS = {
  js: new Set([
    "const", "let", "var", "function", "return", "import", "from", "export",
    "default", "async", "await", "try", "catch", "finally", "if", "else", "for",
    "while", "switch", "case", "break", "continue", "new", "class", "extends",
    "throw", "typeof", "in", "of", "null", "true", "false",
  ]),
  py: new Set([
    "def", "return", "import", "from", "as", "if", "elif", "else", "for", "while",
    "try", "except", "finally", "raise", "with", "class", "True", "False", "None",
  ]),
  cpp: new Set([
    "enum", "void", "bool", "return", "if", "else", "while", "for", "class",
    "struct", "static", "const", "include", "true", "false",
  ]),
  gas: new Set([
    "function", "const", "let", "var", "return", "if", "else", "for", "while",
    "new", "true", "false", "null",
  ]),
};

function langKey(lang) {
  const s = (lang || "").toLowerCase();
  if (s.includes("python")) return "py";
  if (s.includes("c++")) return "cpp";
  if (s.includes("apps script")) return "gas";
  return "js";
}

function tokenize(code, lang) {
  const k = langKey(lang);
  const kw = KEYWORDS[k];

  const src = code || "";
  const out = [];

  // Patterns (ordered by precedence)
  const patterns = [
    // comments
    k === "py"
      ? { type: "com", re: /^#[^\n]*/ }
      : { type: "com", re: /^\/\/[^\n]*/ },

    // strings: "..." '...' `...`
    { type: "str", re: /^`(?:\\.|[^`\\])*`/ },
    { type: "str", re: /^"(?:\\.|[^"\\])*"/ },
    { type: "str", re: /^'(?:\\.|[^'\\])*'/ },

    // numbers
    { type: "num", re: /^\b\d+(\.\d+)?\b/ },

    // identifiers
    { type: "id", re: /^\b[A-Za-z_][A-Za-z0-9_]*\b/ },

    // whitespace
    { type: "ws", re: /^\s+/ },

    // single char fallback
    { type: "ch", re: /^./ },
  ];

  let i = 0;
  while (i < src.length) {
    const chunk = src.slice(i);

    let matched = false;
    for (const p of patterns) {
      const m = chunk.match(p.re);
      if (!m) continue;

      const value = m[0];
      matched = true;

      if (p.type === "id") {
        if (kw.has(value)) {
          out.push({ t: "kw", v: value });
        } else if (
          // a little extra “dev-like” highlighting: capitalize common globals/types
          value === "Date" ||
          value === "JSON" ||
          value === "Intl" ||
          value === "Math" ||
          value === "String" ||
          value === "Number"
        ) {
          out.push({ t: "type", v: value });
        } else {
          out.push({ t: "id", v: value });
        }
      } else {
        out.push({ t: p.type, v: value });
      }

      i += value.length;
      break;
    }

    if (!matched) {
      out.push({ t: "ch", v: src[i] });
      i += 1;
    }
  }

  return out;
}

function HighlightedCode({ code, lang }) {
  const tokens = useMemo(() => tokenize(code, lang), [code, lang]);

  return (
    <>
      {tokens.map((tok, idx) => {
        if (tok.t === "ws") return <span key={idx}>{tok.v}</span>;
        if (tok.t === "id") return <span key={idx} className="tok tok-id">{tok.v}</span>;
        if (tok.t === "kw") return <span key={idx} className="tok tok-kw">{tok.v}</span>;
        if (tok.t === "str") return <span key={idx} className="tok tok-str">{tok.v}</span>;
        if (tok.t === "num") return <span key={idx} className="tok tok-num">{tok.v}</span>;
        if (tok.t === "com") return <span key={idx} className="tok tok-com">{tok.v}</span>;
        if (tok.t === "type") return <span key={idx} className="tok tok-type">{tok.v}</span>;
        return <span key={idx}>{tok.v}</span>;
      })}
    </>
  );
}

export default function Hero() {
  const logos = useMemo(
    () => [
      { src: reactLogo, label: "React" },
      { src: nodejsLogo, label: "Node.js" },
      { src: pythonLogo, label: "Python" },
      { src: gcpLogo, label: "Google Cloud" },
      { src: netlifyLogo, label: "Netlify" },
      { src: githubLogo, label: "GitHub" },
      { src: cppLogo, label: "C/C++" },
      { src: kicadLogo, label: "KiCad" },
      { src: googleLogo, label: "Google Services" },
      { src: mysqlLogo, label: "MySQL" },
      { src: postgresLogo, label: "Postgres" },
    ],
    []
  );

  // Medium length, no easter eggs
  const sequences = useMemo(
    () => [
      {
        lang: "React",
        hint: "UI",
        code:
          `const fmt = (n) => new Intl.NumberFormat().format(n);\n` +
          `\n` +
          `export function KPI({ label, value }) {\n` +
          `  return (\n` +
          `    <div className="kpi">\n` +
          `      <span className="kpi__label">{label}</span>\n` +
          `      <strong className="kpi__value">{fmt(value)}</strong>\n` +
          `    </div>\n` +
          `  );\n` +
          `}\n`,
      },
      {
        lang: "Python",
        hint: "API",
        code:
          `from flask import jsonify, request\n` +
          `\n` +
          `@app.get("/api/metrics")\n` +
          `def metrics():\n` +
          `    user = request.headers.get("X-User")\n` +
          `    rows = db.fetch_all(\n` +
          `        "SELECT k,v FROM metrics WHERE user=%s", (user,)\n` +
          `    )\n` +
          `    return jsonify({ r["k"]: r["v"] for r in rows })\n`,
      },
      {
        lang: "Node.js",
        hint: "Service",
        code:
          `import express from "express";\n` +
          `\n` +
          `const app = express();\n` +
          `app.use(express.json());\n` +
          `\n` +
          `app.post("/api/events", (req, res) => {\n` +
          `  const id = crypto.randomUUID();\n` +
          `  res.status(202).json({ accepted: true, id });\n` +
          `});\n` +
          `\n` +
          `app.get("/health", (_, res) => res.json({ ok: true }));\n`,
      },
      {
        lang: "C++",
        hint: "Embedded",
        code:
          `enum Led { GREEN, WHITE, BLUE, RED };\n` +
          `\n` +
          `void setStatus(bool wifiOk, bool dbOk) {\n` +
          `  if (!wifiOk) return setLed(WHITE);\n` +
          `  setLed(dbOk ? BLUE : RED);\n` +
          `}\n`,
      },
      {
        lang: "Google Apps Script",
        hint: "Excel/Sheets",
        code:
          `function exportRowsToCsv_() {\n` +
          `  const ss = SpreadsheetApp.getActive();\n` +
          `  const sh = ss.getSheetByName("Inventory");\n` +
          `  const values = sh.getDataRange().getValues();\n` +
          `\n` +
          `  const csv = values.map(r => r.map(String).join(",")).join("\\n");\n` +
          `  DriveApp.createFile("inventory_export.csv", csv, MimeType.CSV);\n` +
          `}\n`,
      },
    ],
    []
  );

  const { active, text, cursorOn } = useTypeCycle(sequences, {
    typeMs: 22,
    deleteMs: 14,
    holdMs: 850,
    betweenMs: 240,
  });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="hero" aria-label="Intro">
      <div className="hero__wrap">
        {/* TOP: two columns */}
        <div className="hero__topGrid">
          {/* Left copy */}
          <div className="hero__top">
            <div className="hero__kicker">
              Computer Engineer • Software Development • Cloud
            </div>

            <h1 className="hero__title">Full-stack software, shipped clean.</h1>

            <p className="hero__sub">
              I build frontend, backend, and infrastructure in one workflow — faster
              delivery, fewer handoffs, and lower cost.
            </p>

            <div className="hero__ctaRow">
              <button className="btn btn--primary" onClick={() => scrollTo("contact")}>
                Contact
              </button>

              <button className="btn btn--ghost" onClick={() => scrollTo("projects")}>
                Projects
              </button>

              <a className="hero__link" href="/resume.pdf" target="_blank" rel="noreferrer">
                Resume →
              </a>
            </div>


            <div className="hero__trust">
              <span className="trustItem">Clean UI</span>
              <span className="trustDot" />
              <span className="trustItem">Reliable APIs</span>
              <span className="trustDot" />
              <span className="trustItem">Production-ready deployments</span>
            </div>
          </div>

          {/* Right typing panel */}
          <div className="codePanel" aria-label="Code animation">
            <div className="codePanel__head">
              <div className="codePanel__lang">
                <span className="codePanel__dot" />
                <span>{active.lang}</span>
                <span className="codePanel__hint">{active.hint}</span>
              </div>

              <div className="codePanel__meta">typing…</div>
            </div>

            <pre className="codePanel__pre">
              <code>
                <HighlightedCode code={text} lang={active.lang} />
                <span className={`codeCursor ${cursorOn ? "is-on" : ""}`}>▍</span>
              </code>
            </pre>

            <div className="codePanel__fade" aria-hidden="true" />
          </div>
        </div>

        {/* BOTTOM */}
        <div className="hero__bottom">
          <div className="hero__bullets" aria-label="What you get">
            <div className="bullet">
              <div className="bullet__title">Frontend</div>
              <div className="bullet__desc">Modern UI, dashboards, responsive layouts</div>
            </div>

            <div className="bullet">
              <div className="bullet__title">Backend</div>
              <div className="bullet__desc">APIs, auth, databases, robust workflows</div>
            </div>

            <div className="bullet">
              <div className="bullet__title">Infrastructure</div>
              <div className="bullet__desc">Cloud configs, deployments, scaling basics</div>
            </div>
          </div>

          <div className="hero__stack" aria-label="Tools I use">
            {logos.map((l) => (
              <div key={l.label} className="logoPill" title={l.label}>
                <img className="logoPill__img" src={l.src} alt={l.label} />
                <span className="logoPill__text">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
